import { DateTime } from "luxon";

import type { OrganizationSummaryDto } from "@intelligems/ig-types";

export function isAnOrgSubscriptionActive(orgSummary: OrganizationSummaryDto) {
  return (
    getActiveOrgSubscriptions(orgSummary).length > 0 ||
    !!orgSummary.hasDelayedSubscription
  );
}

export function getActiveOrgSubscriptions(orgSummary: OrganizationSummaryDto) {
  return [
    ...orgSummary.stripeSubscriptionSummaries.filter((ss) =>
      ["active", "trialing", "past_due", "incomplete"].includes(ss.status)
    ),
    ...orgSummary.organizationSubscriptions.filter((os) =>
      ["active", "trialing", "past_due", "incomplete"].includes(os.status)
    ),
  ];
}

/**
 * Whole calendar days from today to the deadline, local midnight to local
 * midnight. `0` means the deadline falls today; negative means it has passed.
 *
 * Display only. A count and a date derived from the same deadline can never
 * disagree, which is not true of rounding a fractional duration. Backend trial
 * provisioning rounds up on purpose and must not be aligned with this.
 */
export const getRemainingDaysUntil = (endTimestampSeconds: number) => {
  const endDay = DateTime.fromSeconds(endTimestampSeconds).startOf("day");
  const today = DateTime.now().startOf("day");

  // A DST transition makes a calendar day 23 or 25 hours, so the diff is a
  // fraction close to a whole number, never an exact one.
  return Math.round(endDay.diff(today, "days").days);
};

/**
 * Display countdown to the `trialing` subscription's `trialEnd`, the Stripe
 * trial deadline verbatim. `0` when there is no trial or no deadline. Never
 * use this to decide access — that is `isOnFreeTrial`.
 */
export const getRemainingTrialDays = (orgSummary: OrganizationSummaryDto) => {
  const freeTrial = orgSummary.stripeSubscriptionSummaries.find(
    (ss) => ss.status === "trialing"
  );

  return freeTrial?.trialEnd != null
    ? getRemainingDaysUntil(freeTrial.trialEnd)
    : 0;
};

/**
 * The entitlement question. Reads subscription status only, never a day
 * count, so rounding can never move an access boundary and a stale cache
 * favours the merchant.
 */
export const isOnFreeTrial = (orgSummary: OrganizationSummaryDto) =>
  orgSummary.stripeSubscriptionSummaries.some((ss) => ss.status === "trialing");

export const getDeprecatedRemainingTrialDays = (
  freeTrialExpiresAtTs: number | null | undefined,
  createdAtTs: number
) => {
  if (freeTrialExpiresAtTs) {
    const remainingDays = Math.floor(
      (freeTrialExpiresAtTs - DateTime.now().toMillis()) / (60 * 60 * 24 * 1000)
    );
    if (remainingDays <= 0) return 0;
    return remainingDays;
  }
  const daysSinceCreation = Math.floor(
    (DateTime.now().toMillis() - createdAtTs) / (60 * 60 * 24 * 1000)
  );

  return Math.max(0, 7 - daysSinceCreation);
};

export function findClosestGreaterValue(
  target: number,
  obj: Record<string, string[]>
): string[] | null {
  const sortedKeys = Object.keys(obj)
    .map((key) => (key === "inf" ? Infinity : Number(key)))
    .filter((key) => !isNaN(key))
    .sort((a, b) => a - b);

  for (const key of sortedKeys) {
    if (key > target) {
      return obj[key === Infinity ? "inf" : key.toString()];
    }
  }

  return obj["inf"] ?? null;
}

/**
 * The org's trial deadline, in epoch seconds, across every subscription
 * summary rather than only a `trialing` one: Stripe drops the `trialing`
 * status the moment the deadline passes, so the sub that carries the trial's
 * end is routinely `past_due` or `active` by the time anyone asks.
 */
export const latestTrialEnd = (
  orgSummary: OrganizationSummaryDto
): number | null => {
  const trialEnds = orgSummary.stripeSubscriptionSummaries
    .map((sub) => sub.trialEnd)
    .filter((trialEnd): trialEnd is number => trialEnd != null);

  if (trialEnds.length === 0) {
    return null;
  }

  return Math.max(...trialEnds);
};

export const hadRecentTrialSubscription = (
  orgSummary: OrganizationSummaryDto
): boolean => {
  const mostRecentTrialEnd = latestTrialEnd(orgSummary);

  if (mostRecentTrialEnd == null) {
    return false;
  }

  const trialEndDate = DateTime.fromSeconds(mostRecentTrialEnd);
  const now = DateTime.now();
  const thirtyDaysAgo = now.minus({ days: 30 });

  return trialEndDate < now && trialEndDate >= thirtyDaysAgo;
};
